import { getFragmentData } from '@packages/gql/generated/shopping';
import { ProductRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductRecommendationFragmentFragmentDoc';
import { SearchBasketToProductsDocument } from '@packages/gql/generated/shopping/SearchBasketToProductsDocument';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoCarouselSkeleton } from '../../RecoCarouselSkeleton';
import { RecoProductCard } from '../../RecoProductCard';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type SearchBasketToProductsRecoSliderProps = Pick<RequestParams, 'aklIds'> &
  ProductsRecoSliderProps;

/* GraphQL */ `
  query SearchBasketToProducts($aklIds: [String!]!, $locale: String!) {
    searchBasketToProducts(aklIds: $aklIds, locale: $locale) {
      title
      type
      recommendations {
        ...ProductRecommendationFragment
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'searchBasketToProducts';

/**
 * SearchSerpToProductRecoSlider component description displayed in storybook
 * */
export const SearchBasketToProductsRecoSlider: FC<SearchBasketToProductsRecoSliderProps> = ({
  elementIndex = 0,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  preferTitle = false,
  aklIds,
  title,
}) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: SearchBasketToProductsDocument,
    variables: {
      locale: locale!,
      aklIds,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.searchBasketToProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) =>
          getFragmentData(ProductRecommendationFragmentFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.searchBasketToProducts.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <div ref={recoEl}>
      {!filteredRecommendations ? (
        <RecoCarouselSkeleton hasNoTitle={preferTitle && title.length === 0} />
      ) : (
        <RecoCarousel
          title={preferTitle ? title : data?.searchBasketToProducts.title || title}
          loadingState={!filteredRecommendations}
        >
          {filteredRecommendations.map((recommendation, idx) => (
            <RecoProductCard
              pageTemplate={pageTemplate}
              // rule is disabled because order doesn't change between rerenders
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              elementIndex={elementIndex}
              position={idx}
              recommendationData={recommendation}
              productsLength={filteredRecommendations.length}
              rdeType={RDE_TYPE}
              onClick={(akl, sku) => {
                onProductClick?.(akl, sku);
              }}
            />
          ))}
        </RecoCarousel>
      )}
    </div>
  );
};
